import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDevices } from '../../context/DeviceContext';
import MetricCard from '../MetricCard/MetricCard';
import { deriveStatus, DEVICE_STATUS, formatLastSeen } from '../../domain/device';
import { calculateHousingBillByLiters } from '../../utils/unitConversion';
import WaterDropRoundedIcon from '@mui/icons-material/WaterDropRounded';
import DevicesRoundedIcon from '@mui/icons-material/DevicesRounded';
import RequestQuoteRoundedIcon from '@mui/icons-material/RequestQuoteRounded';
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';
import SpeedRoundedIcon from '@mui/icons-material/SpeedRounded';
import BlockRoundedIcon from '@mui/icons-material/BlockRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import './AccountOverview.css';

const litros = (n) =>
  Number(n || 0).toLocaleString('es-MX', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

const pesos = (n) =>
  Number(n || 0).toLocaleString('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 });

/* Las métricas usan MetricCard, el componente que ya emplea el resto del panel. */

/** Franja de datos en vivo de un dispositivo dentro de la comparativa. */
function DeviceStats({ device, status }) {
  const abierta = (device.valve_state ?? device.valveState) === 'open';
  const caudal = Number(device.flow_rate_lpm ?? 0);
  const presion = Number(device.pressure_psi ?? 0);
  const alertas = Number(device.alerts_7d ?? 0);
  const enLinea = status === DEVICE_STATUS.ONLINE;

  return (
    <div className="ao-stats">
      <span className="ao-stat" title="Caudal instantáneo">
        <SpeedRoundedIcon fontSize="inherit" />
        {enLinea ? `${caudal.toFixed(1)} L/min` : '—'}
      </span>

      <span className={`ao-stat ${enLinea && abierta && presion < 35 ? 'is-warn' : ''}`} title="Presión de la línea">
        <WaterDropRoundedIcon fontSize="inherit" />
        {enLinea && abierta ? `${presion.toFixed(0)} psi` : abierta ? '—' : 'Sin paso'}
      </span>

      {/* Ausencia de alertas también es información: si sólo se muestran cuando
          las hay, el usuario no sabe si el equipo está bien o si nadie lo vigila. */}
      <span className={`ao-stat ${alertas > 0 ? 'is-warn' : 'is-ok'}`} title="Alertas de los últimos 7 días">
        {alertas > 0 ? <NotificationsActiveRoundedIcon fontSize="inherit" /> : <CheckCircleRoundedIcon fontSize="inherit" />}
        {alertas > 0 ? `${alertas} alerta${alertas > 1 ? 's' : ''}` : 'Sin alertas'}
      </span>

      {!abierta && (
        <span className="ao-stat is-closed" title="Válvula cerrada">
          <BlockRoundedIcon fontSize="inherit" />
          Válvula cerrada
        </span>
      )}
    </div>
  );
}

/**
 * Vista consolidada de la cuenta.
 *
 * Muestra sólo lo que tiene sentido sumar entre dispositivos. El caudal
 * instantáneo, por ejemplo, no se agrega en la fila de métricas: sumar litros
 * por minuto de tomas distintas da un número que no significa nada. Sí aparece
 * por dispositivo, donde sí lo tiene.
 */
export default function AccountOverview() {
  const { devices, summary, selectDevice, loading } = useDevices();
  const navigate = useNavigate();

  const costes = useMemo(() => ({
    mes: calculateHousingBillByLiters(summary.monthlyConsumption),
    dia: calculateHousingBillByLiters(summary.last24hConsumption),
  }), [summary.monthlyConsumption, summary.last24hConsumption]);

  const alertas7d = summary.alerts7d ?? 0;

  if (loading && devices.length === 0) {
    return (
      <section className="ao-root">
        <div className="ao-grid ng-metric-strip">
          {[0, 1, 2, 3].map((i) => <div key={i} className="ao-skeleton" />)}
        </div>
      </section>
    );
  }

  /* Estado vacío: no es un error, es el primer día del usuario. */
  if (devices.length === 0) {
    return (
      <section className="ao-empty">
        <span className="ao-empty__icon"><DevicesRoundedIcon /></span>
        <h3>Aún no tienes dispositivos</h3>
        <p>
          Vincula tu primer NisaMeter para empezar a ver tu consumo, recibir alertas
          de fuga y controlar la válvula a distancia.
        </p>
        <button className="ao-cta" onClick={() => navigate('/devices/link')}>
          <AddRoundedIcon fontSize="small" />
          Vincular mi primer dispositivo
        </button>
      </section>
    );
  }

  const conProblema = devices.filter((d) => deriveStatus(d) !== DEVICE_STATUS.ONLINE);
  const maxConsumo = summary.ranking[0]?.value || 1;

  return (
    <section className="ao-root">
      <div className="ao-grid ng-metric-strip">
        {/* Ventana móvil de 24 h, no día natural: de madrugada el consumo del día
            es de un litro y la cifra parece un fallo. El consumo de hoy sigue
            disponible en el pie de la tarjeta. */}
        <MetricCard
          label="Consumo · últimas 24 h"
          value={litros(summary.last24hConsumption)}
          unit="L"
          accent="accent"
          icon={<WaterDropRoundedIcon />}
          delta={`≈ ${pesos(costes.dia.total)}`}
          footer={`Hoy van ${litros(summary.dailyConsumption)} L · ${summary.total} ${summary.total === 1 ? 'dispositivo' : 'dispositivos'}`}
        />
        <MetricCard
          label="Costo estimado del mes"
          value={pesos(costes.mes.total)}
          tone="info"
          icon={<RequestQuoteRoundedIcon />}
          footer={`${costes.mes.cubicMeters?.toFixed(1) ?? 0} m³ · ${costes.mes.tariffLabel ?? 'Tarifa gubernamental'}`}
        />
        <MetricCard
          label="Dispositivos en línea"
          value={summary.online}
          unit={`de ${summary.total}`}
          tone={summary.online === summary.total ? 'success' : 'warning'}
          icon={<DevicesRoundedIcon />}
          delta={summary.offline > 0 ? `${summary.offline} sin conexión` : 'Todos reportando'}
        />
        <MetricCard
          label="Alertas en 7 días"
          value={alertas7d}
          tone={alertas7d > 0 ? 'warning' : 'success'}
          icon={alertas7d > 0 ? <NotificationsActiveRoundedIcon /> : <CheckCircleRoundedIcon />}
          delta={alertas7d > 0 ? 'Requiere revisión' : 'Sin incidencias'}
          footer={
            summary.valvesClosed > 0
              ? `${summary.valvesClosed} ${summary.valvesClosed === 1 ? 'válvula cerrada' : 'válvulas cerradas'}`
              : 'Todas las válvulas abiertas'
          }
        />
      </div>

      {/* Sólo aparece cuando hay algo que atender. Un aviso permanente que casi
          siempre dice "todo bien" deja de leerse. */}
      {conProblema.length > 0 && (
        <div className="ao-attention" role="status">
          <span className="ao-attention__title">
            {conProblema.length === 1
              ? 'Un dispositivo necesita atención'
              : `${conProblema.length} dispositivos necesitan atención`}
          </span>
          <div className="ao-attention__list">
            {conProblema.map((d) => (
              <button key={d.device_id} className="ao-attention__item" onClick={() => selectDevice(d.device_id)}>
                <span className={`ao-dot ao-dot--${deriveStatus(d)}`} />
                <span className="ao-attention__name">{d.device_name || d.device_id}</span>
                <span className="ao-attention__meta">{formatLastSeen(d)}</span>
                <ArrowForwardRoundedIcon fontSize="small" />
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="ao-compare">
        <header className="ao-compare__head">
          <div>
            <h3 className="section-title">Consumo por dispositivo</h3>
            <p className="ao-compare__sub">Últimas 24 horas, con su costo estimado y estado en vivo</p>
          </div>
          <button className="ao-link" onClick={() => navigate('/devices')}>
            Administrar <ArrowForwardRoundedIcon fontSize="small" />
          </button>
        </header>

        <ul className="ao-bars">
          {summary.ranking.map(({ device, value }, i) => {
            const pct = maxConsumo > 0 ? Math.max(0.02, value / maxConsumo) : 0.02;
            const status = deriveStatus(device);
            const coste = calculateHousingBillByLiters(value);
            const cuota = summary.last24hConsumption > 0
              ? Math.round((value / summary.last24hConsumption) * 100)
              : 0;

            return (
              <li key={device.device_id} className="ao-bar" style={{ '--i': i }}>
                <button className="ao-bar__btn" onClick={() => selectDevice(device.device_id)}>
                  <div className="ao-bar__head">
                    <span className="ao-bar__name">
                      <span className={`ao-dot ao-dot--${status}`} />
                      {device.device_name || device.device_id}
                      {device.usage_profile && (
                        <span className="ao-bar__profile">{device.usage_profile}</span>
                      )}
                    </span>
                    <span className="ao-bar__figures">
                      <span className="ao-bar__value">{litros(value)} L</span>
                      <span className="ao-bar__cost">{pesos(coste.total)}</span>
                    </span>
                  </div>

                  <div className="ao-bar__track">
                    <div className="ao-bar__fill" style={{ '--pct': pct }} />
                  </div>

                  <div className="ao-bar__foot">
                    <DeviceStats device={device} status={status} />
                    <span className="ao-bar__share">
                      {status === DEVICE_STATUS.ONLINE ? `${cuota}% del total` : formatLastSeen(device)}
                    </span>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
